/**
 * The group copy's clipboard (19/10/2026): the campaign group copied on one
 * country's board, kept in this browser so any other board can offer the
 * paste — across tabs too. Storage is a convenience: when it is blocked the
 * copy still lives in this tab's memory, and nothing ever throws.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { GroupCopyRef } from '@trafficpulse/shared';

export interface GroupCopyClip {
  source: GroupCopyRef;
  countryName: string;
  /** The campaign group's label. */
  label: string;
  masterName: string;
  containers: number;
  /** ISO instant. */
  copiedAt: string;
}

export const CLIP_STORAGE_KEY = 'trafficpulse-automation-group-copy';
/** Fired on this window after a write — the storage event only reaches the other tabs. */
export const CLIP_EVENT = 'trafficpulse-automation-group-copy';

let memory: GroupCopyClip | null = null;
/** The last write could not reach storage — this tab reads its memory copy. */
let memoryOnly = false;

const isText = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

export function isClip(v: unknown): v is GroupCopyClip {
  if (!v || typeof v !== 'object') return false;
  const c = v as Partial<GroupCopyClip>;
  const src = c.source as Partial<GroupCopyRef> | undefined;
  const names = isText(c.label) && typeof c.countryName === 'string' && typeof c.masterName === 'string';
  return Boolean(src && isText(src.countryKey) && isText(src.groupKey)) && names && typeof c.containers === 'number';
}

export function readClip(): GroupCopyClip | null {
  if (memoryOnly) return memory;
  try {
    const raw = localStorage.getItem(CLIP_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isClip(parsed) ? parsed : null;
  } catch {
    return memory;
  }
}

const announce = () => {
  try {
    window.dispatchEvent(new Event(CLIP_EVENT));
  } catch {
    // No window (tests without a DOM): nobody to tell.
  }
};

export function writeClip(clip: GroupCopyClip): void {
  memory = clip;
  try {
    localStorage.setItem(CLIP_STORAGE_KEY, JSON.stringify(clip));
    memoryOnly = false;
  } catch {
    // Private window or blocked storage: the copy stays in this tab only.
    memoryOnly = true;
  }
  announce();
}

export function clearClip(): void {
  memory = null;
  try {
    localStorage.removeItem(CLIP_STORAGE_KEY);
    memoryOnly = false;
  } catch {
    // Blocked storage: the memory copy is gone, that is enough.
  }
  announce();
}

/** The bar offers the paste anywhere but on the copied group itself. */
export function pasteOffered(clip: GroupCopyClip | null, here: { countryKey: string; groupKey: string | null }): boolean {
  if (!clip) return false;
  return clip.source.countryKey !== here.countryKey || clip.source.groupKey !== here.groupKey;
}
